import { useCartDialog } from '../../app/_providers/CartProvider.client'
import { useTranslation } from '../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../brunswick/containers/cart'
import { KitEmbroideryService } from '../../brunswick/containers/embroidery'
import { MultiAddToBagService } from '../../brunswick/containers/multi-add-to-bag'
import { Variant } from '../../types/graphql'

export function useBagMultiple({
  discountPrice,
  hasItemWithMissingSize,
  isKitFinalSale,
  kitId,
  kitName,
  kitSku,
  price,
  swPromoEligible,
  variants,
}: {
  discountPrice?: number | undefined | null
  hasItemWithMissingSize: boolean
  isKitFinalSale: boolean
  kitId: string
  kitName: string
  kitSku: string
  price: number
  swPromoEligible: boolean | undefined
  variants: ReadonlyArray<Variant> | null | undefined
}) {
  const { addItems, cart } = CartContainer.useContainer()
  const { open: openCartDialog } = useCartDialog()
  const kitEmbroideryService = KitEmbroideryService.useContainer()
  const multiAddToBagService = MultiAddToBagService.useContainer()
  const {
    cart: { finalSale, nonReturnable },
  } = useTranslation()

  //This property is used as a visible (no underscore prefix) line item on the minicart and checkout page
  const finalSaleProperty = {
    [finalSale]: nonReturnable,
  }

  //If a kit is discounted, pass a line item property to each item, with the same discount percentage.
  //The cart script will apply this discount to each item
  const discountPct = 100 - ((discountPrice ?? price) / price) * 100
  const validDiscountPct = !isNaN(discountPct) && discountPct > 0 && discountPct <= 100
  const discountProperty =
    discountPrice && validDiscountPct ? { _kitDiscountPct: `${discountPct}` } : {}

  async function handleAddToBagClick(): Promise<void> {
    if (hasItemWithMissingSize) {
      multiAddToBagService.setAddToBagErrors({ missingRequiredSize: true })
      /**
       * We must wait for the React state update/re-render before [data-is-invalid=true] will
       * be applied to the invalid size section title element. So we schedule the following
       * logic to be run in the next iteration of the event loop:
       */
      setTimeout(() => {
        const sizeSections = document.getElementsByClassName('size-section-title')
        const elementWithError = Array.from(sizeSections).find(
          el => el.getAttribute('data-has-error') === 'true'
        ) as HTMLElement | undefined
        if (elementWithError) {
          try {
            elementWithError.scrollIntoView({ behavior: 'smooth', block: 'start' })
            elementWithError.focus({ preventScroll: true })
          } catch {}
        }
      }, 0)
    } else {
      await addToBag()
    }
    return Promise.resolve()
  }

  function resetInvalidBagClickStatus(): void {
    multiAddToBagService.setAddToBagErrors({ missingRequiredSize: false })
  }

  async function addToBag(): Promise<void> {
    if (!variants || variants.length < 1) return
    multiAddToBagService.setIsAddToCartInFlight(true)
    const _swPromoEligible: string = swPromoEligible ? 'true' : 'false'

    const kitProperties = {
      _kit: kitName,
      _kitSku: kitSku,
      _kitId: kitId,
      _swPromoEligible,
    }

    const itemsToAdd = variants
      .map(variant => {
        const variantGid: string = variant.shopifyId
        const embroideryOptions = kitEmbroideryService.getEmbroideryCartOption(variantGid)
        const finalSale = variant?.product?.finalSale || isKitFinalSale

        if (embroideryOptions && embroideryOptions.valid) {
          const items = [
            {
              item: embroideryOptions.embroideryItemVariant,
              properties: {
                _kitId: kitId,
                _swPromoEligible,
                ...embroideryOptions.embroideryLineItemProperties,
                ...finalSaleProperty,
              },
              swPromoEligible,
            },
            {
              item: variant,
              properties: {
                ...kitProperties,
                ...discountProperty,
                ...finalSaleProperty,
                _embroideryId: variantGid,
                _guid: embroideryOptions.embroideryLineItemProperties._guid,
                _LPROP: embroideryOptions.embroideryLineItemProperties._LPROP,
                _logo_id: embroideryOptions.embroideryLineItemProperties._logo_id,
              },
              swPromoEligible,
            },
          ]
          let logoEmbroiderySetupInCart = false
          for (let key in cart?.items) {
            if (
              cart?.items[key]?.properties?._logo_setup_embroideryId ===
              embroideryOptions.embroideryLineItemProperties._logo_setup_embroideryId
            ) {
              logoEmbroiderySetupInCart = true
              break
            }
          }

          if (!logoEmbroiderySetupInCart && embroideryOptions.embroiderySetupVariant) {
            items.push({
              item: embroideryOptions.embroiderySetupVariant,
              properties: {
                ...kitProperties,
                ...(embroideryOptions.embroideryLineItemProperties._logo_setup_embroideryId && {
                  _embroideryId:
                    embroideryOptions.embroideryLineItemProperties._logo_setup_embroideryId,
                }),
                ...(embroideryOptions.embroideryLineItemProperties._logo_setup_guid && {
                  _guid: embroideryOptions.embroideryLineItemProperties._logo_setup_guid,
                }),
                _is_logo_setup: 'true',
                _logo_id: embroideryOptions.embroideryLineItemProperties._logo_id,
              },
              swPromoEligible,
            })
          }
          return items
        } else {
          return {
            finalSale,
            item: variant,
            properties: {
              ...kitProperties,
              ...discountProperty,
              ...(finalSale ? finalSaleProperty : {}),
            },
            swPromoEligible,
          }
        }
      })
      .flat()

    await addItems(itemsToAdd)

    multiAddToBagService.setIsAddToCartInFlight(false)
    openCartDialog()
  }

  return {
    handleAddToBagClick,
    resetInvalidBagClickStatus,
  }
}
